/** @jsxImportSource @emotion/react */

/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { css as cssClassName } from '@emotion/css';
import styled from '@emotion/styled';
import { css, Stack } from '@mui/material';
import { FC } from 'react';
import { toast as toastLib, ToastOptions } from 'react-toastify';
import { ReactComponent as IconError } from './icons/error.svg';
import { ReactComponent as IconWarn } from './icons/warning.svg';
import { ReactComponent as IconClose } from './icons/close.svg';
import { ReactComponent as IconSVGSuccess } from './icons/success.svg';
import { ReactComponent as IconSVGNotification } from './icons/notification.svg';

interface TextProps {
  mt?: number;
  mb?: number;
  pt?: number;
  pb?: number;
  lh?: number;
  fz?: number;
  weight?: number;
}

const H5 = styled.h5<TextProps>`
  font-weight: ${({ weight }) => weight ?? 700};
  font-size: ${({ fz }) => `${fz ?? '16'}px`};
  line-height: ${({ lh }) => `${lh ?? ''}px`};
  margin-top: ${({ mt }) => `${mt ?? 0}px`};
  margin-bottom: ${({ mb }) => `${mb ?? 0}px`};
  color: #4b5563;
`;
H5.defaultProps = {
  mb: 0,
  mt: 0,
  lh: 20,
};
const Text = styled.p<TextProps>`
  padding: ${({ pb, pt }) => `${pt ?? 0}px 0 ${pb ?? 0}px`};
  margin: ${({ mb, mt }) => `${mt ?? 0}px 0 ${mb ?? 0}px`};
  font-size: ${({ fz }) => `${fz ?? 0}px`};
  color: ${({ color }) => color};
  line-height: ${({ lh }) => `${lh ?? 0}px`};
  font-weight: ${({ weight }) => weight ?? 0};
  color: #4b5563;
`;
Text.defaultProps = {
  mb: 0,
  mt: 0,
  fz: 16,
  lh: 20,
  weight: 400,
};

const IconCloseBtn = () => {
  return (
    <div
      css={css`
        height: 24px;
        width: 24px;
        display: flex;
        justify-content: center;
        align-items: center;
      `}
    >
      <IconClose />
    </div>
  );
};

// error

const ErrorToastContent: FC<{ header?: string; text? }> = ({ header, text }) => {
  return (
    <Stack>
      <H5 lh={18} fz={14} weight={700} mb={4}>
        {header ?? 'Error!'}
      </H5>
      <Text lh={18} fz={14} weight={500}>
        {text}
      </Text>
    </Stack>
  );
};

const errorToast = (data: { header?: string; text }, options?: ToastOptions) => {
  toastLib.error(() => <ErrorToastContent {...data} />, {
    icon: <IconError />,
    closeButton: <IconCloseBtn />,
    draggable: true,
    closeOnClick: true,
    pauseOnHover: true,
    ...options,
  });
  //toastLib.update(data.text, options);
};

// warn

const WarnToastContent: FC<{ header?: string; text?: string }> = ({ header, text }) => {
  return (
    <>
      <H5 lh={18} fz={14} weight={700} mb={4}>
        {header ?? 'Attention!'}
      </H5>
      <Text lh={18} fz={14} weight={500}>
        {text}
      </Text>
    </>
  );
};
const customWarnStyles = cssClassName`
    border: 1px solid #FAB41C;
    .Toastify {
        &__progress-bar {
            background: #FAB41C;
        }
    }
`;
const warningToast = (data: { header?: string; text: string }) => {
  toastLib.warn(() => <WarnToastContent {...data} />, {
    type: 'default',
    className: customWarnStyles,
    icon: <IconWarn />,
    closeButton: <IconCloseBtn />,
  });
};

// success

const SuccessToastContent: FC<{ header?: string; text?: string }> = ({ header, text }) => {
  return (
    <>
      <H5 lh={18} fz={14} weight={700} mb={4}>
        {header ?? 'Success!'}
      </H5>
      <Text lh={18} fz={14} weight={500}>
        {text}
      </Text>
    </>
  );
};

const successToast = (data: { header?: string; text: string }) => {
  toastLib.success(() => <SuccessToastContent {...data} />, {
    progressStyle: { backgroundColor: '#05B7AC' },
    icon: <IconSVGSuccess />,
    closeButton: <IconCloseBtn />,
    autoClose: 5000,
  });
};

const NotificationToastContent: FC<{ header?: string; text?: string }> = ({ header, text }) => {
  return (
    <>
      <H5 lh={18} fz={14} weight={700} mb={4}>
        {header ?? 'Notification!'}
      </H5>
      <Text lh={18} fz={14} weight={500}>
        {text}
      </Text>
    </>
  );
};
const customNotificationStyles = cssClassName`
    border: 1px solid #19A1B7;
    .Toastify {
        &__progress-bar {
            background: #19A1B7;
        }
    }
`;
const notificationToast = (data: { header?: string; text: string }) => {
  toastLib.success(() => <NotificationToastContent {...data} />, {
    type: 'default',
    className: customNotificationStyles,
    icon: <IconSVGNotification />,
    closeButton: <IconCloseBtn />,
    autoClose: 5000,
  });
};

export const toast = ({
  variant,
  message: text,
  title: header,
}: {
  variant: 'error' | 'success' | 'warning' | 'notification';
  message: string;
  title?: string;
}) => {
  switch (variant) {
    case 'error': {
      errorToast({ text, header });
      break;
    }
    case 'warning': {
      warningToast({ text, header });
      break;
    }
    case 'success': {
      successToast({ text, header });
      break;
    }
    case 'notification': {
      notificationToast({ text, header });
      break;
    }
    default: {
      break;
    }
  }
};
